import React, { useEffect, useRef, useState } from 'react';
import { website_whats_new } from '../services';
import { subHeading3, body, bodySmall, label, caption, fontWeight, spacing } from '../Constants/Fonts';
import { theme } from '../Constants/Colors';

// All styling is inline (no SCSS). Typography comes from the Fonts.js presets
// ('Outfit', sans-serif) + spacing scale; colours come from the shared default
// theme in Colors.js. Only fixed layout dimensions are literal.
const S = {
    wrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    btn: {
        background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
        color: '#fff', width: 40, height: 40, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.15s ease',
    },
    panel: {
        position: 'absolute', top: 50, right: 0, width: 380, maxHeight: 520,
        background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: spacing.xl,
        boxShadow: theme.shadow, zIndex: 50,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: `${spacing['2xl']}px ${spacing['3xl']}px`,
        borderBottom: `1px solid ${theme.border}`, background: theme.headerBg,
    },
    panelTitle: subHeading3({ fontWeight: fontWeight.bold, color: theme.titleText }),
    panelBody: { flex: 1, overflowY: 'auto' },
    state: body({
        padding: `${spacing['4xl']}px ${spacing['3xl']}px`,
        textAlign: 'center', color: theme.mutedText,
    }),
    stateError: body({
        padding: `${spacing['4xl']}px ${spacing['3xl']}px`,
        textAlign: 'center', color: theme.danger,
    }),
    item: {
        padding: `${spacing['2xl']}px ${spacing['3xl']}px`,
        borderBottom: `1px solid ${theme.itemBorder}`,
    },
    meta: {
        display: 'flex', alignItems: 'center', gap: spacing.lg,
        marginBottom: spacing.md, flexWrap: 'wrap',
    },
    tag: caption({
        fontWeight: fontWeight.bold, background: theme.successBg, color: theme.successText,
        padding: `${spacing.xs}px ${spacing.md}px`, borderRadius: spacing.sm, letterSpacing: '0.4px',
    }),
    category: label({
        fontWeight: fontWeight.semibold, color: theme.bodyText,
        textTransform: 'uppercase', letterSpacing: '0.3px',
    }),
    date: label({ fontWeight: fontWeight.regular, color: theme.mutedText, marginLeft: 'auto' }),
    title: body({ fontWeight: fontWeight.semibold, color: theme.titleText, marginBottom: spacing.sm, lineHeight: 1.35 }),
    desc: bodySmall({ color: theme.bodyText, lineHeight: 1.5, marginBottom: spacing.lg }),
};

const WhatsNewPopover = () => {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fetched, setFetched] = useState(false);
    const [btnHover, setBtnHover] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await website_whats_new();
            const data = res?.statusCode?.response_data?.data;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching whats-new:', err);
            setError('Could not load updates.');
        } finally {
            setLoading(false);
            setFetched(true);
        }
    };

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        if (next && !fetched) loadItems();
    };

    const formatDate = (raw) => {
        if (!raw) return '';
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return raw;
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div style={S.wrapper} ref={wrapperRef}>
            <button
                type="button"
                style={{ ...S.btn, background: btnHover ? 'rgba(255, 255, 255, 0.08)' : 'transparent' }}
                onClick={handleToggle}
                onMouseEnter={() => setBtnHover(true)}
                onMouseLeave={() => setBtnHover(false)}
                aria-label="What's new"
            >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 11l18-8v18l-18-8z" />
                    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
                </svg>
            </button>

            {open && (
                <div style={S.panel}>
                    <div style={S.header}>
                        <span style={S.panelTitle}>What's New</span>
                    </div>

                    <div style={S.panelBody}>
                        {loading ? (
                            <div style={S.state}>Loading…</div>
                        ) : error ? (
                            <div style={S.stateError}>{error}</div>
                        ) : items.length === 0 ? (
                            <div style={S.state}>Nothing new yet.</div>
                        ) : (
                            items.map((item, idx) => (
                                <div key={`${item.date}-${idx}`} style={S.item}>
                                    <div style={S.meta}>
                                        {item.tag && <span style={S.tag}>{item.tag}</span>}
                                        {item.category && <span style={S.category}>{item.category}</span>}
                                        {item.date && <span style={S.date}>{formatDate(item.date)}</span>}
                                    </div>
                                    {item.title && <div style={S.title}>{item.title}</div>}
                                    {item.description && <div style={S.desc}>{item.description}</div>}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsNewPopover;
